import filter from 'lodash/filter'
import indexOf from 'lodash/indexOf'
import noop from 'lodash/noop'
import React, { ChangeEventHandler, SetStateAction, useCallback } from 'react'

import { getPersonalizationTranslationHeader } from '@Components/Personalization/Modals/Shared'
import { UpSellItem } from '@Domains/Product/Features/Display/UpSellItem/UpSellItem'
import { Product, TacticUpgradeWith } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

import styles from './UpgradeWith.module.scss'

export const UpgradeWith = ({
  buyPersonalizationID,
  items,
  onBuyPersonalizationID = noop,
  selectedCrossSellIDs = [],
  setSelectedCrossSellIDs,
}: {
  buyPersonalizationID?: string | null
  items: TacticUpgradeWith['items']
  onBuyPersonalizationID?: (personalizationID: string | null) => void
  selectedCrossSellIDs?: Product['id'][]
  setSelectedCrossSellIDs?: SetStateAction<any>
}) => {
  const { t } = useTranslation()
  const isSelected = useCallback((id: Product['id']): boolean => indexOf(selectedCrossSellIDs, id) >= 0, [selectedCrossSellIDs])

  const onBuyPersonalizationChangeEvent: ChangeEventHandler<HTMLInputElement> = useCallback(({
    currentTarget: {
      checked,
      value,
    },
  }) => {
    if (checked) {
      onBuyPersonalizationID(value)
    } else {
      onBuyPersonalizationID(null)
    }
  }, [onBuyPersonalizationID])

  const onCheckboxChange: ChangeEventHandler<HTMLInputElement> = useCallback(({
    currentTarget: {
      checked,
      value,
    },
  }) => {
    if (!setSelectedCrossSellIDs) {
      return
    }

    if (checked) {
      setSelectedCrossSellIDs([...selectedCrossSellIDs, value])
    } else {
      setSelectedCrossSellIDs(filter(selectedCrossSellIDs, currentID => currentID !== value))
    }
  }, [selectedCrossSellIDs, setSelectedCrossSellIDs])

  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className={styles.upSells}>
      <div className={styles.title}>
        {t('products:WB_UPGRADE_WITH')}
      </div>
      <div className={styles.wrapper}>
        {items.map((item) => {
          if (!item || !item.product) {
            return null
          }

          if (item.type === 'giftWrapping') {
            return (
              <UpSellItem
                key={`cross_sell_product_${item.product.id}`}
                onChange={onCheckboxChange}
                product={item.product}
                selected={isSelected(item.product.id)}
                type="giftWrappings"
              />
            )
          }

          if (item.type === 'personalization') {
            return (
              <UpSellItem
                key={`cross_sell_personalizations_${item.product.id}`}
                onChange={onBuyPersonalizationChangeEvent}
                product={{
                  id: item.product.id ?? '',
                  masterVariant: item.product.masterVariant,
                  primaryAsset: (item.product as any).primaryAsset,
                  url: null,
                  name: t(getPersonalizationTranslationHeader(item.product as any)),
                }}
                selected={buyPersonalizationID === item.product.id}
              />
            )
          }

          return (
            <UpSellItem
              key={`cross_sell_product_${item.product.id}`}
              onChange={onCheckboxChange}
              product={item.product || undefined}
              selected={isSelected(item.product.id)}
            />
          )
        })}
      </div>
    </div>
  )
}
